import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { DateRangePicker } from '../ui/DateRangePicker';
import { useReports } from '../../hooks/useReports';
import { useProjects, useProjectConnections } from '../../hooks/useProjects';
import { 
  X, 
  FileText, 
  Calendar, 
  Users, 
  BarChart3, 
  TrendingUp, 
  Target,
  Globe,
  Mail,
  Smartphone,
  ShoppingCart,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Loader,
  Database,
  AlertCircle,
  Search,
  FolderOpen,
  Clock
} from 'lucide-react';

interface CreateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateReportModal({ isOpen, onClose }: CreateReportModalProps) {
  const [step, setStep] = useState(1);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [reportData, setReportData] = useState({
    name: '',
    description: '',
    report_type: 'custom' as 'custom' | 'template',
    template_id: '',
    schedule: 'none' as 'none' | 'daily' | 'weekly' | 'monthly',
    dateRange: {
      type: 'last_30_days' as 'last_7_days' | 'last_30_days' | 'last_90_days' | 'custom',
      start: '',
      end: ''
    },
    config: {
      widgets: [] as string[]
    }
  });

  // Get projects for project selection
  const { projects } = useProjects();
  
  // Get project connections for data source filtering
  const { data: connectionsData } = useProjectConnections(selectedProjectId);
  const availableDataSources = connectionsData?.availableDataSources || [];

  // Set default project when projects load
  useEffect(() => {
    if (projects && projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  const { useTemplates, createReport, generateReport, isCreating } = useReports();
  const { data: templatesData, isLoading: templatesLoading } = useTemplates();

  // Data source display names and icons - Update to check for google_analytics instead of ga4
  const dataSourceInfo = {
    google_analytics: { name: 'Google Analytics 4', icon: BarChart3, color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30' },
    ga4: { name: 'Google Analytics 4', icon: BarChart3, color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30' },
    search_console: { name: 'Search Console', icon: Search, color: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30' },
    google_ads: { name: 'Google Ads', icon: Target, color: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30' },
    facebook_ads: { name: 'Facebook Ads', icon: Users, color: 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30' },
    shopify: { name: 'Shopify', icon: ShoppingCart, color: 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30' },
    hubspot: { name: 'HubSpot', icon: Mail, color: 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30' }
  };

  const getIconComponent = (iconName: string | React.ComponentType<any>) => {
    // If it's already a React component, return it
    if (typeof iconName === 'function') {
      return iconName;
    }
    
    // Map string icon names from backend to React components
    const iconMap: Record<string, React.ComponentType<any>> = {
      // Backend icon names to React components
      'chart': TrendingUp,
      'speedometer': BarChart3,
      'target': Target,
      'users': Users,
      'share': Users,
      'cog': Target,
      'search': Globe,
      'analytics': BarChart3,
      'performance': BarChart3,
      'conversion': Target,
      'audience': Users,
      'seo': Globe,
      'social': Users,
      'custom': FileText,
      // Original frontend mappings
      'TrendingUp': TrendingUp,
      'Globe': Globe,
      'Target': Target,
      'Users': Users,
      'ShoppingCart': ShoppingCart,
      'Mail': Mail,
      'Smartphone': Smartphone,
      'BarChart3': BarChart3
    };
    return iconMap[iconName] || FileText;
  };

  const getTemplateReportType = (templateId: string): string => {
    // Map template IDs to their corresponding report types
    const templateTypeMap: Record<string, string> = {
      'analytics': 'analytics',
      'marketing-overview': 'analytics',
      'performance': 'performance',
      'conversion': 'conversion',
      'audience': 'audience',
      'seo': 'seo',
      'social': 'social'
    };
    
    return templateTypeMap[templateId] || 'analytics';
  };

  // Default templates as fallback - Update requiredConnections to use google_analytics
  const defaultTemplates = [
    {
      id: 'marketing-overview',
      name: 'Marketing Overview',
      description: 'Comprehensive overview of all marketing channels',
      icon: TrendingUp,
      widgets: ['Traffic Sources', 'Conversion Rates', 'Campaign Performance', 'ROI Analysis'],
      popular: true,
      requiredConnections: ['google_analytics'],
      optionalConnections: ['google_ads']
    },
    {
      id: 'traffic-analysis',
      name: 'Website Traffic Analysis',
      description: 'Deep dive into website traffic patterns',
      icon: Globe,
      widgets: ['Page Views', 'Unique Visitors', 'Bounce Rate', 'Session Duration'],
      popular: true,
      requiredConnections: ['google_analytics'],
      optionalConnections: []
    },
    {
      id: 'conversion-funnel',
      name: 'Conversion Funnel Report',
      description: 'Track user journey from awareness to conversion',
      icon: Target,
      widgets: ['Funnel Stages', 'Drop-off Points', 'Conversion Rates'],
      popular: false,
      requiredConnections: ['google_analytics'],
      optionalConnections: ['google_ads']
    },
    {
      id: 'social-media',
      name: 'Social Media Performance',
      description: 'Analyze social media engagement and ROI',
      icon: Users,
      widgets: ['Engagement Rate', 'Follower Growth', 'Post Performance'],
      popular: true,
      requiredConnections: [],
      optionalConnections: ['facebook_ads', 'google_analytics']
    }
  ];

  const templates = templatesData?.templates || defaultTemplates;

  // Widget definitions with data source requirements - Update to use google_analytics
  const availableWidgets = [
    { 
      id: 'traffic-sources', 
      name: 'Traffic Sources', 
      icon: Globe, 
      requiredDataSource: 'google_analytics',
      description: 'Breakdown of website traffic by source'
    },
    { 
      id: 'conversion-rates', 
      name: 'Conversion Rates', 
      icon: Target, 
      requiredDataSource: 'google_analytics',
      description: 'Track conversion performance across goals'
    },
    { 
      id: 'campaign-performance', 
      name: 'Campaign Performance', 
      icon: TrendingUp, 
      requiredDataSource: 'google_ads',
      description: 'Track advertising campaign performance'
    },
    { 
      id: 'roi-analysis', 
      name: 'ROI Analysis', 
      icon: BarChart3, 
      requiredDataSource: 'google_analytics',
      description: 'Return on investment tracking'
    },
    { 
      id: 'social-engagement', 
      name: 'Social Engagement', 
      icon: Users, 
      requiredDataSource: 'facebook_ads',
      description: 'Social media engagement metrics'
    },
    { 
      id: 'ecommerce-performance', 
      name: 'E-commerce Performance', 
      icon: ShoppingCart, 
      requiredDataSource: 'shopify',
      description: 'Online store sales and conversion data'
    }
  ];

  const getAvailableWidgets = () => {
    return availableWidgets.filter(widget => 
      availableDataSources.includes(widget.requiredDataSource)
    );
  };

  const canUseTemplate = (template: any) => {
    return template.requiredConnections.every((conn: string) => availableDataSources.includes(conn));
  };

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find((t: any) => t.id === templateId);
    if (template) {
      setReportData({
        ...reportData,
        template_id: templateId,
        name: template.name,
        description: template.description,
        config: {
          widgets: template.widgets || []
        }
      });
    }
  };

  const handleWidgetToggle = (widgetId: string) => {
    setReportData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        widgets: prev.config.widgets.includes(widgetId)
          ? prev.config.widgets.filter(id => id !== widgetId)
          : [...prev.config.widgets, widgetId]
      }
    }));
  };

  const handleCreate = async () => {
    try {
      const reportConfig = {
        ...reportData,
        projectId: selectedProjectId,
        reportType: reportData.report_type === 'template' 
          ? getTemplateReportType(reportData.template_id)
          : 'custom',
        config: {
          ...reportData.config,
          dateRange: reportData.dateRange,
          metrics: [
            'sessions',
            'users', 
            'pageviews',
            'bounceRate',
            'sessionDuration',
            'conversionRate'
          ],
          includeInsights: true
        },
        schedule: reportData.schedule !== 'none' ? reportData.schedule : null,
        isScheduled: reportData.schedule !== 'none'
      };

      const result = await createReport(reportConfig);
      
      // Optionally generate the report immediately
      if (result.report?.id) {
        await generateReport(result.report.id);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to create report:', error);
      alert('Failed to create report. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <div className="p-6 flex-1 overflow-y-auto min-h-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Create New Report</h2>
              <p className="text-gray-600 dark:text-gray-400">Step {step} of 3</p>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Project Selector */}
          {projects && projects.length > 0 && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <FolderOpen className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Project:</span>
                  </div>
                  <select
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {projects.map((project: any) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                {availableDataSources.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Connected:</span>
                    {availableDataSources.slice(0, 3).map((source: string) => {
                      const info = dataSourceInfo[source as keyof typeof dataSourceInfo];
                      const IconComponent = info?.icon || Database;
                      return (
                        <div key={source} className={`px-2 py-1 rounded text-xs ${info?.color || 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'}`}>
                          <IconComponent className="w-3 h-3 inline mr-1" />
                          {info?.name || source.toUpperCase()}
                        </div>
                      );
                    })}
                    {availableDataSources.length > 3 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">+{availableDataSources.length - 3} more</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Progress</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{Math.round((step / 3) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 3) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Step Content */}
          <div className="pb-6">
            {/* Step 1: Choose Type */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">How would you like to create your report?</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Template Option */}
                    <Card 
                      className={`p-6 cursor-pointer border-2 transition-colors ${
                        reportData.report_type === 'template' 
                          ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                      }`}
                      onClick={() => setReportData({ ...reportData, report_type: 'template' })}
                    >
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-r from-blue-100 dark:from-blue-900/30 to-purple-100 dark:to-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Use a Template</h4>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Start with a pre-built template and customize it to your needs
                        </p>
                        <div className="flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">
                          <Sparkles className="w-4 h-4 mr-1" />
                          Recommended for beginners
                        </div>
                      </div>
                    </Card>

                    {/* Custom Option */}
                    <Card 
                      className={`p-6 cursor-pointer border-2 transition-colors ${
                        reportData.report_type === 'custom' 
                          ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                      }`}
                      onClick={() => setReportData({ ...reportData, report_type: 'custom' })}
                    >
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-r from-green-100 dark:from-green-900/30 to-blue-100 dark:to-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                          <BarChart3 className="w-8 h-8 text-green-600 dark:text-green-400" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Build from Scratch</h4>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Create a completely custom report with your choice of widgets
                        </p>
                        <div className="flex items-center justify-center text-sm text-green-600 dark:text-green-400">
                          <Target className="w-4 h-4 mr-1" />
                          Full customization
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Choose Template or Configure Custom */}
            {step === 2 && (
              <div className="space-y-6">
                {reportData.report_type === 'template' ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Choose a Template</h3>
                    
                    {templatesLoading ? (
                      <div className="text-center py-8">
                        <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600 dark:text-blue-400" />
                        <p className="text-gray-600 dark:text-gray-300">Loading templates...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto pr-2">
                        {templates.map((template: any) => {
                          const IconComponent = getIconComponent(template.icon);
                          const canUse = canUseTemplate(template);
                          const isComingSoon = template.comingSoon;
                          const isDisabled = !canUse || isComingSoon;
                          
                          return (
                            <Card 
                              key={template.id}
                              className={`p-4 cursor-pointer transition-shadow duration-200 ${
                                isDisabled
                                  ? 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-700/50' 
                                  : 'hover:shadow-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600'
                              }`}
                              onClick={() => !isDisabled && handleTemplateSelect(template.id)}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  isDisabled
                                    ? 'bg-gray-200 dark:bg-gray-600' 
                                    : 'bg-gradient-to-r from-blue-100 dark:from-blue-900/30 to-purple-100 dark:to-purple-900/30'
                                }`}>
                                  <IconComponent className={`w-5 h-5 ${
                                    isDisabled ? 'text-gray-500 dark:text-gray-400' : 'text-blue-600 dark:text-blue-400'
                                  }`} />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className={`font-semibold ${
                                      isDisabled ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'
                                    }`}>{template.name}</h4>
                                    <div className="flex space-x-1">
                                      {template.popular && !isComingSoon && (
                                        <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs px-2 py-1 rounded-full">
                                          Popular
                                        </span>
                                      )}
                                      {isComingSoon && (
                                        <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-xs px-2 py-1 rounded-full flex items-center">
                                          <Clock className="w-3 h-3 mr-1" />
                                          Coming Soon
                                        </span>
                                      )}
                                      {!canUse && !isComingSoon && (
                                        <span className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs px-2 py-1 rounded-full flex items-center">
                                          <AlertCircle className="w-3 h-3 mr-1" />
                                          Requires Connection
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <p className={`text-sm mb-3 ${
                                    isDisabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'
                                  }`}>
                                    {template.description}
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {template.widgets?.slice(0, 3).map((widget: string, index: number) => (
                                      <span 
                                        key={index}
                                        className={`text-xs px-2 py-1 rounded ${
                                          isDisabled 
                                            ? 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400' 
                                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                        }`}
                                      >
                                        {widget}
                                      </span>
                                    ))}
                                    {template.widgets?.length > 3 && (
                                      <span className={`text-xs px-2 py-1 rounded ${
                                        isDisabled 
                                          ? 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400' 
                                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                      }`}>
                                        +{template.widgets.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Configure Your Custom Report</h3>
                    
                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="report-name" className="text-gray-700 dark:text-gray-300">Report Name</Label>
                        <Input
                          id="report-name"
                          placeholder="Enter report name"
                          value={reportData.name}
                          onChange={(e) => setReportData({ ...reportData, name: e.target.value })}
                          className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="report-description" className="text-gray-700 dark:text-gray-300">Description</Label>
                        <Input
                          id="report-description"
                          placeholder="Describe what this report will track"
                          value={reportData.description}
                          onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                          className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        />
                      </div>

                      <div>
                        <Label className="text-gray-700 dark:text-gray-300 mb-3 block">Select Widgets</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                          {getAvailableWidgets().map((widget) => {
                            const isSelected = reportData.config.widgets.includes(widget.id);
                            const widgetDataSourceInfo = dataSourceInfo[widget.requiredDataSource as keyof typeof dataSourceInfo];
                            const isAvailable = availableDataSources.includes(widget.requiredDataSource);
                            
                            return (
                              <div
                                key={widget.id}
                                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                  isSelected
                                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                    : isAvailable
                                    ? 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                                    : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 opacity-60 cursor-not-allowed'
                                }`}
                                onClick={() => isAvailable && handleWidgetToggle(widget.id)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <widget.icon className={`w-5 h-5 ${
                                      isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                                    }`} />
                                    <div>
                                      <h4 className={`font-medium ${
                                        isSelected ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                                      }`}>
                                        {widget.name}
                                      </h4>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">{widget.description}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    {isSelected && <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
                                    <div className={`px-2 py-1 rounded text-xs ${
                                      isAvailable
                                        ? widgetDataSourceInfo?.color || 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
                                        : 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
                                    }`}>
                                      <Database className="w-3 h-3 mr-1 inline" />
                                      {widgetDataSourceInfo?.name || widget.requiredDataSource.toUpperCase()}
                                      {!isAvailable && ' (not connected)'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {getAvailableWidgets().length === 0 && (
                        <div className="text-center py-8">
                          <AlertCircle className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Widgets Available</h4>
                          <p className="text-gray-600 dark:text-gray-300 mb-4">
                            Connect data sources to unlock widgets for your custom reports.
                          </p>
                          <Button 
                            variant="outline"
                            onClick={() => {
                              window.location.href = `/projects/${selectedProjectId}/settings?tab=connections`;
                            }}
                            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Connect Data Sources
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Date Range Selection */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-300">Date Range</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Select the time period for your report data</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { value: 'last_7_days', label: 'Last 7 Days' },
                      { value: 'last_30_days', label: 'Last 30 Days' },
                      { value: 'last_90_days', label: 'Last 90 Days' },
                      { value: 'custom', label: 'Custom Range' }
                    ].map((option) => (
                      <div
                        key={option.value}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          reportData.dateRange.type === option.value
                            ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                        }`}
                        onClick={() => setReportData({ 
                          ...reportData, 
                          dateRange: { 
                            ...reportData.dateRange, 
                            type: option.value as any 
                          } 
                        })}
                      >
                        <div className="text-center">
                          <Calendar className={`w-5 h-5 mx-auto mb-2 ${
                            reportData.dateRange.type === option.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                          }`} />
                          <span className={`text-sm font-medium ${
                            reportData.dateRange.type === option.value ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                          }`}>
                            {option.label}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Custom Date Range Inputs */}
                  {reportData.dateRange.type === 'custom' && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="start-date" className="text-gray-700 dark:text-gray-300">Start Date</Label>
                        <Input
                          id="start-date"
                          type="date"
                          value={reportData.dateRange.start}
                          onChange={(e) => setReportData({
                            ...reportData,
                            dateRange: {
                              ...reportData.dateRange,
                              start: e.target.value
                            }
                          })}
                          className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <Label htmlFor="end-date" className="text-gray-700 dark:text-gray-300">End Date</Label>
                        <Input
                          id="end-date"
                          type="date"
                          value={reportData.dateRange.end}
                          onChange={(e) => setReportData({
                            ...reportData,
                            dateRange: {
                              ...reportData.dateRange,
                              end: e.target.value
                            }
                          })}
                          className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300">Schedule (Optional)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                    {['none', 'daily', 'weekly', 'monthly'].map((scheduleOption) => (
                      <div
                        key={scheduleOption}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          reportData.schedule === scheduleOption
                            ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                        }`}
                        onClick={() => setReportData({ ...reportData, schedule: scheduleOption as any })}
                      >
                        <div className="text-center">
                          <Calendar className={`w-5 h-5 mx-auto mb-2 ${
                            reportData.schedule === scheduleOption ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                          }`} />
                          <span className={`text-sm font-medium capitalize ${
                            reportData.schedule === scheduleOption ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                          }`}>
                            {scheduleOption === 'none' ? 'One-time' : scheduleOption}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
          <div className="flex space-x-3">
            {step > 1 && (
              <Button variant="outline" onClick={handleBack} className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                Back
              </Button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose} className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              Cancel
            </Button>
            
            {step < 3 ? (
              <Button 
                onClick={handleNext}
                disabled={
                  step === 1 ? false :
                  step === 2 && reportData.report_type === 'custom' && (!reportData.name || !reportData.description)
                }
                className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleCreate}
                disabled={
                  isCreating || 
                  (reportData.report_type === 'custom' && reportData.config.widgets.length === 0)
                }
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-500 dark:to-purple-500 dark:hover:from-blue-600 dark:hover:to-purple-600 text-white"
              >
                {isCreating ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Report
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
} 